import React, { memo, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Alert, Select, Spin } from 'antd';

import styled from 'styled-components';
import fontService, { TextType } from 'services/FontService';
import { desktop, mobile } from 'services/ui';
import { Colors } from 'context';
import { CareerIcon, ToastTypes } from 'atoms';
import { CareerLockedSection } from 'organisms';
import {
  APPLICATION_STATUSES,
  CareerApplicationStatus,
  ICareerApplication,
  ICareerApplicationInput,
  getCareerJourneyErrorText,
  useCareerApplicationActions,
  useCareerApplications,
  useCareerSectionAccess,
  useCareerVacancyFeed,
  useToaster,
} from 'core';

import { PATHS } from 'router/config';
import { ApplicationScenario, applicationScenarioOf } from 'router/careerLinks';
import { careerLayout, careerType } from 'services/careerType';
import { CTA_TEXT } from 'services/careerTokens';
import { CareerApplicationCard } from './parts/CareerApplicationCard';
import { CareerApplicationForm } from './parts/CareerApplicationForm';

/**
 * `/career/applications` — журнал поиска работы (ТЗ 0069).
 *
 * До него система теряла человека ровно там, где начинается самое важное:
 * после клика «Открыть вакансию» он уходил на чужой сайт, и дальше ни ответа,
 * ни интервью, ни отказа мы не знали. Отсюда же брался разрыв в воронке — шаг
 * «Откликнулись» не пополнялся с тех пор, как самоотметку убрали.
 *
 * 🔴 Никакой автоматики. Отклик появляется здесь только по явному действию
 * человека: у нас нет ни одного надёжного сигнала «он откликнулся», и
 * выдуманный отравил бы и журнал, и оценку готовности.
 */
const TOAST_KEY = 'career-applications';

export const CareerApplications = memo(() => {
  const { t } = useTranslation();
  const { addToast } = useToaster();
  const [status, setStatus] = useState<string>('');
  const { applications, loading, error } = useCareerApplications(status || undefined);
  const {
    trackApplication,
    reviseApplication,
    moveApplication,
    dropApplication,
    loading: saving,
  } = useCareerApplicationActions();

  const [editing, setEditing] = useState<ICareerApplication | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [scenario, setScenario] = useState<ApplicationScenario>('sent');

  const { locked, pending } = useCareerSectionAccess('applications');

  const openForm = (next: ApplicationScenario) => {
    setEditing(null);
    setScenario(next);
    setFormOpen(true);
  };
  // Есть ли что предложить в ленте — только для пустого журнала. Тот же
  // запрос ленты без фильтров; пока журнал не пуст, он не уходит вовсе.
  const { totalAvailable, loading: feedLoading } = useCareerVacancyFeed({
    paused: loading || applications.length > 0 || Boolean(status) || locked || pending,
  });
  const noVacancies = !feedLoading && totalAvailable === 0;

  // ТЗ 0071, п. 8.1 и 0072: «Zapisz wysłaną aplikację» ведёт сюда с
  // `?add=sent` (старый `?add=1` — то же самое), «Planuję» — с `?add=planned`,
  // и форма открывается сразу нужным сценарием. Параметр снимается
  // `replace`-ом: иначе обновление страницы или «Назад» открывали бы форму
  // снова и снова.
  const [searchParams, setSearchParams] = useSearchParams();
  const wantsAdd = applicationScenarioOf(searchParams.get('add'));
  useEffect(() => {
    if (!wantsAdd || locked || pending) return;
    setEditing(null);
    setScenario(wantsAdd);
    setFormOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('add');
    setSearchParams(next, { replace: true });
  }, [wantsAdd, locked, pending, searchParams, setSearchParams]);

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('careerCenter.applications.filterAll') },
      ...APPLICATION_STATUSES.map((code) => ({
        value: code,
        label: t(`careerCenter.applications.status.${code}`),
      })),
    ],
    [t],
  );

  // Один ключ на весь экран: тосты журнала вытесняют друг друга, а не копятся
  // столбиком после нескольких быстрых действий подряд.
  const report = (errorCode: string | null) => {
    if (errorCode) {
      addToast(TOAST_KEY, {
        type: ToastTypes.danger,
        // По КОДУ, а не по тексту сервера: сообщение сервера русское.
        text: getCareerJourneyErrorText(t, errorCode),
      });
      return false;
    }
    return true;
  };

  const submit = async (input: ICareerApplicationInput) => {
    const result = editing
      ? await reviseApplication(editing.id, input)
      : await trackApplication(input);
    if (!report(result.errorCode)) {
      return;
    }
    if (result.duplicate) {
      addToast(TOAST_KEY, {
        type: ToastTypes.warning,
        text: t('careerCenter.applications.duplicate'),
      });
    }
    setFormOpen(false);
    setEditing(null);
  };

  const changeStatus = async (application: ICareerApplication, next: CareerApplicationStatus) => {
    const result = await moveApplication(application.id, next);
    report(result.errorCode);
  };

  const remove = async (application: ICareerApplication) => {
    const result = await dropApplication(application.id);
    // 🔴 Успех — это `success`, а не «кода ошибки не пришло». Ответ без кода и
    // без успеха рапортовал бы «удалено» о том, чего не произошло.
    if (report(result.errorCode) && result.success) {
      addToast(TOAST_KEY, {
        type: ToastTypes.success,
        text: t('careerCenter.applications.deleted'),
      });
    }
  };

  if (locked || pending) {
    return (
      <Wrapper>
        {locked ? (
          <CareerLockedSection capability="applications" />
        ) : (
          <Center>
            <Spin size="large" />
          </Center>
        )}
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Header>
        <HeaderText>
          <PageTitle>{t('careerCenter.applications.title')}</PageTitle>
          <PageInfo>{t('careerCenter.applications.lead')}</PageInfo>
        </HeaderText>
        {/* Пустой журнал несёт свою пару действий ниже; вторая пара в шапке
            дала бы экрану два главных действия (ТЗ 0071, 2.2). */}
        {applications.length > 0 || status ? (
          <HeaderActions>
            <PrimaryButton type="button" onClick={() => openForm('sent')}>
              {t('careerCenter.applications.addSent')}
            </PrimaryButton>
            <SecondaryButton type="button" onClick={() => openForm('planned')}>
              {t('careerCenter.applications.addPlanned')}
            </SecondaryButton>
          </HeaderActions>
        ) : null}
      </Header>

      <Filter
        value={status}
        onChange={(value) => setStatus(value as string)}
        options={statusOptions}
        aria-label={t('careerCenter.applications.statusLabel')}
      />

      {error ? <Alert type="error" showIcon message={t('careerCenter.journey.loadError')} /> : null}

      {loading && applications.length === 0 ? (
        <Center>
          <Spin size="large" />
        </Center>
      ) : null}

      {!loading && applications.length === 0 && status ? (
        <Muted>{t('careerCenter.applications.emptyStatus')}</Muted>
      ) : null}

      {/* ТЗ 0071, п. 8.2: пустой журнал — не тупик. Первое действие —
          найти вакансию; уже отправленный вне LearnIT отклик — вторым. Если
          вакансий в LearnIT нет, их меняют местами: основным становится запись
          внешнего отклика, иначе главная кнопка ведёт в пустую ленту. */}
      {!loading && !error && applications.length === 0 && !status ? (
        <EmptyCard>
          <EmptyIcon aria-hidden>
            <CareerIcon name="applications" size={28} />
          </EmptyIcon>
          <EmptyTitle>{t('careerCenter.applications.emptyTitle')}</EmptyTitle>
          <EmptyLead>
            {noVacancies
              ? t('careerCenter.applications.emptyLeadNoVacancies')
              : t('careerCenter.applications.emptyLead')}
          </EmptyLead>
          <EmptyActions>
            {noVacancies ? (
              <>
                <PrimaryButton type="button" onClick={() => openForm('sent')}>
                  {t('careerCenter.applications.emptyAddSent')}
                </PrimaryButton>
                <SecondaryLink to={PATHS.careerJobs}>
                  {t('careerCenter.applications.emptyFind')}
                </SecondaryLink>
              </>
            ) : (
              <>
                <PrimaryLink to={PATHS.careerJobs}>
                  {t('careerCenter.applications.emptyFind')}
                </PrimaryLink>
                <SecondaryButton type="button" onClick={() => openForm('sent')}>
                  {t('careerCenter.applications.emptyHaveSent')}
                </SecondaryButton>
              </>
            )}
          </EmptyActions>
        </EmptyCard>
      ) : null}

      <List>
        {applications.map((application) => (
          <CareerApplicationCard
            key={application.id}
            application={application}
            disabled={saving}
            onStatus={(next) => changeStatus(application, next)}
            onEdit={() => {
              setEditing(application);
              setFormOpen(true);
            }}
            onDelete={() => remove(application)}
          />
        ))}
      </List>

      <CareerApplicationForm
        open={formOpen}
        application={editing}
        scenario={scenario}
        saving={saving}
        onSubmit={submit}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
      />
    </Wrapper>
  );
});

const Wrapper = styled.div`
  /* width: 100% обязателен: шаблон это flex-колонка с align-items:
     flex-start, и без ширины страница схлопывается по содержимому. */
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 0 0;

  ${desktop} {
    padding: 32px 0 0;
  }
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

/* Кнопки по содержимому справа от заголовка; на узком экране — под ним. */
const HeaderActions = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
`;

const HeaderText = styled.div`
  flex: 1 1 320px;
  min-width: 0;
`;

const PageTitle = styled.h1`
  margin: 0;
  ${careerType.h1()};
`;

const PageInfo = styled.p`
  margin: 8px 0 0;
  max-width: 640px;
  color: ${Colors.gray_dark};
  ${careerType.body()};
`;

const Filter = styled(Select)`
  width: 220px;

  ${mobile} {
    width: 100%;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Muted = styled.p`
  margin: 0;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const EmptyCard = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
  padding: 40px ${careerLayout.cardPadding}px;
  border: 1px dashed ${Colors.system_gray};
  border-radius: ${careerLayout.cardRadius}px;
  text-align: center;
`;

const EmptyIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${Colors.bg_gray};
`;

const EmptyTitle = styled.h2`
  margin: 0;
  ${careerType.h2()};
`;

const EmptyLead = styled.p`
  margin: 0;
  max-width: 560px;
  color: ${Colors.gray_dark};
  ${careerType.body()};
`;

const EmptyActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 12px 24px;
  margin-top: 8px;
`;

const primaryLook = `
  display: inline-flex;
  align-items: center;
  min-height: ${careerLayout.primaryButton}px;
  padding: 0 24px;
  border: 0;
  border-radius: ${careerLayout.controlRadius}px;
  cursor: pointer;
`;

/* Основное действие — жёлтая заливка с тёмным текстом, как главная кнопка плана. */
const PrimaryLink = styled(Link)`
  ${primaryLook};
  background: ${Colors.main};
  color: ${CTA_TEXT};
  ${careerType.bodyStrong()};

  &:hover {
    color: ${CTA_TEXT};
    background: ${Colors.main_dark};
  }
`;

const PrimaryButton = styled.button`
  ${primaryLook};
  background: ${Colors.main};
  color: ${CTA_TEXT};
  ${careerType.bodyStrong()};

  &:hover {
    background: ${Colors.main_dark};
  }
`;

const SecondaryLink = styled(Link)`
  min-height: ${careerLayout.hitTarget}px;
  display: inline-flex;
  align-items: center;
  color: ${Colors.black};
  text-decoration: underline;
  ${careerType.bodyStrong()};

  &:hover {
    color: ${Colors.black};
  }
`;

const SecondaryButton = styled.button`
  min-height: ${careerLayout.hitTarget}px;
  padding: 0;
  border: 0;
  background: none;
  color: ${Colors.black};
  text-decoration: underline;
  cursor: pointer;
  ${careerType.bodyStrong()};
`;
